import React from "react";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import type { EvaluationDomain, OperandDto, WhenDto } from "../../../../../../types/resolver";
import { WhenBuilder } from "../WhenBuilder";
import { COLLECTION_KEYS } from "./constants";

interface AggregateOperandProps {
    value: OperandDto | null | undefined;
    onChange: (operand: OperandDto) => void;
    evaluationDomain?: EvaluationDomain;
    declaredDerivedAttributeNames?: string[];
}

const DEFAULT_WHEN_TRUE: WhenDto = { true: true };

export const AggregateOperand: React.FC<AggregateOperandProps> = ({
    value,
    onChange,
    evaluationDomain,
    declaredDerivedAttributeNames = [],
}) => {
    const operation = value?.operation ?? "COUNT";
    const over = value?.over ?? "codes";
    const when = value?.when ?? null;

    const update = (patch: Partial<OperandDto>) =>
        onChange({ operandType: "aggregate", operation, over, ...value, ...patch });

    return (
        <Stack spacing={1}>
            <FormControl size="small" fullWidth>
                <InputLabel>Operation</InputLabel>
                <Select
                    label="Operation"
                    value={operation}
                    onChange={(e) => update({ operation: e.target.value })}
                >
                    <MenuItem value="COUNT">COUNT</MenuItem>
                </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
                <InputLabel>Over</InputLabel>
                <Select
                    label="Over"
                    value={over}
                    onChange={(e) => update({ over: e.target.value })}
                >
                    {COLLECTION_KEYS.map((k) => (
                        <MenuItem key={k.value} value={k.value}>
                            {k.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {when == null ? (
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => update({ when: DEFAULT_WHEN_TRUE })}
                >
                    Add filter condition
                </Button>
            ) : (
                <Box>
                    <Typography variant="caption">Filter condition (optional)</Typography>
                    <WhenBuilder
                        value={when}
                        onChange={(w) => update({ when: w ?? undefined })}
                        depth={1}
                        evaluationDomain={evaluationDomain}
                        declaredDerivedAttributeNames={declaredDerivedAttributeNames}
                    />
                    <Button
                        size="small"
                        color="error"
                        onClick={() => update({ when: undefined })}
                    >
                        Remove filter
                    </Button>
                </Box>
            )}
        </Stack>
    );
};
